import { Component, ViewChild, ChangeDetectionStrategy, inject, OnInit, signal, effect, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedModule } from '@app/shared/shared.module';
import { NotesSuccessPopupComponent } from '@app/pages/notes/notes-success-popup/notes-success-popup.component';
import { NotesCommonFunctions } from '@app/pages/notes/util/notes-common-function';
import { Router } from '@angular/router';
import { CourseService } from '../services/course.service';
import { take } from 'rxjs';
import { PageNavigationComponent } from '@app/shared/components/page-navigation/page-navigation.component';
import { Utilities } from '@app/core/utils/utilities';
import { CourseData, CourseListingPageData } from '../models/course-data.model';
import { CommonFunctions } from '@app/shared/util/common-functions';
import { CourseConfirmationPopupComponent } from '../course-confirmation-popup/course-confirmation-popup.component';
import { MatDialog } from '@angular/material/dialog';
import { CourseTypeDialog, NoCourseComponent } from '../no-course/no-course.component';
import { ManageFilterComponent } from '@app/shared/components/manage-filter/manage-filter.component';
import { CourseListingFilterData } from '@app/shared/enm/course-instance.enum';
import { ConstantVariables } from '@app/shared/util/constant-variables';
import { EasyLoadService } from '@app/shared/services/easy-load.service';
import { ReportConstants } from '@app/pages/subject-report/util/report-constants';
import { LoaderService } from '@app/core/services/loader.service';
import { CourseCommonFunctions } from '../course-util/course-common-functions';
import { SuccessOrWarningPopupComponent } from '@app/shared/components/success-or-warning-popup/success-or-warning-popup.component';

@Component({
  selector: 'app-course-listing-table-details',
  imports: [CommonModule, SharedModule, NotesSuccessPopupComponent, PageNavigationComponent, NoCourseComponent, ManageFilterComponent, SuccessOrWarningPopupComponent],
  templateUrl: './course-listing-table-details.component.html',
  styleUrl: './course-listing-table-details.component.css'
})
export class CourseListingTableDetailsComponent implements OnInit {
  @ViewChild(MatSort) sort!: MatSort;
  @Input() courseErDialogData: any;
  @Output() closeCourseListingPopup = new EventEmitter<any>();

  constructor() { this.loadCoursePage(); }

  util = inject(Utilities);
  router = inject(Router);
  dialog = inject(MatDialog);
  notesCommonFn = inject(NotesCommonFunctions);
  courseDataService = inject(CourseService);
  commonFn = inject(CommonFunctions);
  constVar = inject(ConstantVariables);
  easyLoadService = inject(EasyLoadService);
  reportConstants = inject(ReportConstants);
  loaderService = inject(LoaderService);
  courseCommonFunction = inject(CourseCommonFunctions);

  pageNumber$ = signal<number>(1);
  totalRecordCount$ = signal<number>(0);
  showCoursesPage$ = signal<any>(null);
  courseData$ = signal<any>(null);
  hideShowFilterOption$ = signal<boolean>(false);
  courseListDetails$ = signal<any>(null);

  upDatedEvent: any;
  searchCourseFields: any = {};
  dataSource = signal<any>(null);
  courseRefDetails: any = [];
  courseTypeDropdownDetails = signal<any>(null);
  courseListingFilterDetails = CourseListingFilterData;
  displayedColumns: string[] = ['courseId', 'courseName', 'courseInstanceCount', 'status', 'courseType', 'applicableForFastlane', 'courseCodeType', 'courseCode', 'studyLevel', 'qualification', 'menu'];
  courseTypeId: any;
  institutionDetails = JSON.parse(this.util.institutionDetails());
  countryId = JSON.parse(localStorage.getItem("countryDetails") ?? '')?.countryId;
  successWarningPopupType = signal<string>('');
  successOrWarnMsg = signal<string>('');

  ngOnInit(): void {
    this.fetchCourseListingDetailsOnLoad();
  }

  fetchCourseListingDetailsOnLoad() {
    this.initCoursePageWithData();
    if (this.util.saveCourseSuccessPopup$() || this.util.saveSuccessPopup$())
      this.showSuccessMsg();
    this.fetchCourseFilterDropdownDetails(this.courseListingFilterDetails.CourseTypeFieldId, '', '', 'onLoad');
  }

  initCoursePageWithData(sortEvent?: Sort) {
    this.totalRecordCount$.set(0);
    const erQualId = this.courseErDialogData?.erData?.erQualificationId;
    this.showCoursesPage$.set(((Object.keys(this.searchCourseFields)?.length || sortEvent?.active) ? 'list' : ''));
    this.courseDataService.getCoursesData(this.pageNumber$(), sortEvent?.active, sortEvent?.direction, this.searchCourseFields, erQualId)
      .pipe(take(1))
      .subscribe({
        next: (res: CourseListingPageData) => {
          if (res?.courses) this.dataSource.set(new MatTableDataSource(res.courses));
          this.courseListDetails$.set(res)
          this.totalRecordCount$.set(res?.totalRecordCount || 0);
          this.showCoursesPage$.set(this.totalRecordCount$() ? this.totalRecordCount$() : this.fetchFilterData());
        },
        error: (err: Error) => console.log(err.message)
      });
  }

  fetchFilterData() {
    return (Object.keys(this.searchCourseFields)?.length ? 'no-result-filter' : 'no-data')
  }

  onTableSortedOrderChange(sortEvent: Sort) {
    this.upDatedEvent = this.commonFn.updateSortingdetails(sortEvent);
    this.pageNumber$.set(1);
    this.initCoursePageWithData(this.upDatedEvent);
  }

  getPaginationDetails(pageNo: number) {
    this.pageNumber$.set(pageNo);
    this.initCoursePageWithData(this.upDatedEvent);
  }

  loadCoursePage() {
    effect(() => {
      if (this.notesCommonFn.successPopup() === 'reload')
        this.initCoursePageWithData(this.upDatedEvent);
    }, { allowSignalWrites: true })
  }

  showSuccessMsg() {
    setTimeout(() => {
      if (this.util.saveCourseSuccessPopup$())
        this.util.saveCourseSuccessPopup$.set('');
      else if (this.util.saveSuccessPopup$())
        this.util.saveSuccessPopup$.set('');
    }, 5000);
  }

  showCourseInstanceList(courseData: any) {
    this.courseTypeId = this.courseTypeDropdownDetails()?.find((courseType: any) => courseType.name === courseData.courseType).id;
    this.router.navigateByUrl(`/manage-course/instance/${this.courseTypeId}/${courseData.courseId}`);
  }

  viewNotes(entityId: number) {
    this.router.navigateByUrl('/view-notes/Course/' + entityId);
  }

  openConfirmationPopup(course: CourseData) {
    this.util.isDialogOpen$.set(true);
    const dialogRef = this.dialog.open(CourseConfirmationPopupComponent, {
      panelClass: 'course-conf-popup',
      data: { ...course, level: 'Course', fastlaneEnabled: course.applicableForFastlane }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      this.util.isDialogOpen$.set(false);
      if (result) {
        this.initCoursePageWithData(this.upDatedEvent);
        this.util.saveCourseSuccessPopup$.set(result);
        this.showSuccessMsg();
      }
    });
  }

  createNewCourse() {
    this.util.isDialogOpen$.set(true);
    const dialogRef = this.dialog.open(CourseTypeDialog);
    dialogRef.afterClosed().subscribe(result => {
      this.util.isDialogOpen$.set(false);
    })
  }

  showCourseFilterOption(fieldId: string, fieldName: string, fieldType: string) {
    if (fieldType === 'AJAX' || fieldId === this.constVar.applicableForFastlane)
      this.fetchCourseAjaxReferenceData(fieldId, fieldName, fieldType)
    else if (fieldType === 'SELECT_BOX')
      this.fetchCourseFilterDropdownDetails(fieldId, fieldName, fieldType)
    else
      this.showCourseFilterDetails(fieldId, fieldName, fieldType);
  }

  fetchCourseAjaxReferenceData(fieldId: string, fieldName: string, fieldType: string) {
    if (!this.courseRefDetails[fieldId?.toLowerCase()])
      this.courseDataService.getCourseAjaxReferenceData(fieldId).subscribe({
        next: (data: any) => {
          const courseAjaxDetails = data.referenceTypes;
          courseAjaxDetails.forEach((refTypeValues: any) => {
            this.courseRefDetails[refTypeValues?.name?.toLowerCase()] = refTypeValues.values;
            this.showCourseFilterDetails(fieldId, fieldName, fieldType);
          });
        },
        error: (error: any) => {
          console.log("course filter ajax reference data err-", error);
        }
      })
    else
      this.showCourseFilterDetails(fieldId, fieldName, fieldType);
  }

  fetchCourseFilterDropdownDetails(fieldId: string, fieldName: string, fieldType: string, from?: any) {
    if (fieldId === this.courseListingFilterDetails.CourseTypeFieldId && !this.courseTypeDropdownDetails())
      this.fetchInstanceReferenceData(fieldId, fieldName, fieldType, from)
    else if (!this.courseRefDetails[fieldId?.toLowerCase()])
      this.courseDataService.getCourseReferenceData().subscribe({
        next: (data: any) => {
          const courseDropdownDetails = data.referenceTypes;
          courseDropdownDetails.forEach((refdata: any) => {
            this.courseRefDetails[[this.courseListingFilterDetails.StudyLevelRefName, this.courseListingFilterDetails.QualificationTypeRefName].includes(refdata?.name) ? refdata.name.toLowerCase() + 'id' : refdata?.name?.toLowerCase()] = refdata.values;
            this.showCourseFilterDetails(fieldId, fieldName, fieldType);
          })
        },
        error: (error: any) => {
          console.log("course filter reference data err-", error);
        }
      })
    else
      this.showCourseFilterDetails(fieldId, fieldName, fieldType);
  }

  fetchInstanceReferenceData(fieldId: string, fieldName: string, fieldType: string, from?: any) {
    this.courseDataService.getInstanceReferenceData().subscribe({
      next: (data: any) => {
        const courseInstanceRefTypes = data.referenceTypes;
        courseInstanceRefTypes.forEach((courseInstanceRefDetails: any) => {
          if (courseInstanceRefDetails.name === this.courseListingFilterDetails.CourseTypeRefName)
            this.courseTypeDropdownDetails.set(courseInstanceRefDetails.values.filter((courseTypeData: any) => courseTypeData.parentCourseTypeId === null));
          if (!from)
            this.showCourseFilterDetails(fieldId, fieldName, fieldType);
        })
      },
      error: (err: any) => {
        console.log("course filter instance reference data err-", err);
      }
    })
  }

  showCourseFilterDetails(fieldId: any, fieldName: any, fieldType: any) {
    this.hideShowFilterOption$.set(true);
    const courseFilterData = {
      filterId: fieldId,
      filterType: fieldType,
      filterName: fieldName,
      referenceDetails: fieldId === this.courseListingFilterDetails.CourseTypeFieldId ? this.courseTypeDropdownDetails() : this.courseRefDetails[fieldId?.toLowerCase()]
    }
    this.courseData$.set(courseFilterData);
  }

  clearCourseFilter(fieldId?: string) {
    if (fieldId)
      delete this.searchCourseFields[fieldId];
    else
      this.searchCourseFields = {};
    this.pageNumber$.set(1);
    this.initCoursePageWithData(this.upDatedEvent);
  }

  searchAppliedCourseFilter(event: any) {
    this.closeCourseFilterPopup();
    this.pageNumber$.set(1);
    if (event.filterBy === this.courseListingFilterDetails.CourseCodeFieldId)
      this.searchCourseFields[event.filterBy] = String(event.filterValue)?.toUpperCase();
    else
      this.searchCourseFields[event.filterBy] = event.filterValue
    this.initCoursePageWithData(this.upDatedEvent);
  }

  closeCourseFilterPopup() {
    this.hideShowFilterOption$.set(false);
  }

  redirectToEditPage(courseData: any, pageType?: string) {
    let url = "/edit-course/";
    if (courseData.status?.toLowerCase() === 'archived' || pageType?.includes('view'))
      url = "/view-course/";
    this.courseTypeId = this.courseTypeDropdownDetails()?.find((courseType: any) => courseType.name === courseData.courseType).id;
    this.router.navigateByUrl(url + this.courseTypeId + '/' + courseData.courseId);
  }

  openInstanceListPopup(courseData: any) {
    const courseTypeId = this.courseTypeDropdownDetails()?.find((courseType: any) => courseType.name === courseData.courseType).id;
    this.closeCourseListingPopup.emit({ ...courseData, courseTypeId: courseTypeId, erDialogData: this.courseErDialogData });
  }

  downloadStagesModulesReport(state: 'init' | 'download') {
    if (state === 'init')
      this.courseCommonFunction.downloadStagesModulesReport(this.institutionDetails?.institutionId).subscribe({
        next: (response: any) => {
          if (response?.statusCode === 200) {
            this.showMessage('success', 'The modules report has been successfully initiated.');
            this.initCoursePageWithData(this.upDatedEvent);
          } else {
            this.showMessage('warning', 'Unable to initiate download. Please try again later');
          }
        },
        error: () => this.showMessage('warning', 'Unable to initiate download. Please try again later')
      });
    else if (state === 'download')
      this.easyLoadService.getDownloadReportPreSignedUrl(this.courseListDetails$()?.fileName ?? "", this.reportConstants.STAGES_MODULE_REPORT)?.subscribe({
        next: (res: any) => {
          this.loaderService.forceCloseOverlay();
          const preSignedUrl = res?.download_url;
          if (preSignedUrl) {
            this.util.downloadFile(preSignedUrl, this.courseListDetails$()?.fileName);
            this.showMessage('success', 'The modules report has been successfully downloaded.');
          }
        },
        error: (err: Error) => console.log('ERROR : Error getting download presigned url', err)
      });
  }

  openUploadPopup() {
    this.courseCommonFunction.openUploadPopup(this.institutionDetails?.institutionId)?.subscribe((uploadResponse: any) => {
      this.util.isDialogOpen$.set(false);
      if (uploadResponse) {
        this.showMessage('success', 'Modules report is successfully uploaded.');
        this.initCoursePageWithData(this.upDatedEvent);
      }
    })
  }

  showMessage(popType?: string, message?: string) {
    this.successWarningPopupType.set(popType ?? '');
    this.successOrWarnMsg.set(message ?? '');
    if (message)
      setTimeout(() => {
        this.showMessage();
      }, 5000);
  }
}
